// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
// import { connect } from "react-redux"; // Greif
import { css } from "styled-components";

/* Starter Custom Code - Start */
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
// import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table"; // Greif
/* Starter Custom Code - End */

/* Greif Custom Code - Start */
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import isTrueOrUndefined from "@store/Framework/Common/Utilities/isTrueOrUndefined";
import { historyTableProps } from "@store/Themes/common/props/history";
import { connect, ResolveThunks } from "react-redux";
/* Greif Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // Greif
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    addToCartPopupTimeout: getSettingsCollection(state).cartSettings.addToCartPopupTimeout,
});

/* Greif Custom Code - Start */
const mapDispatchToProps = {
    reorder,
};
/* Greif Custom Code - End */

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // Greif
    showOrderNumber?: boolean;
    showDate?: boolean;
    showOrderTotal?: boolean;
    showStatus?: boolean;
    showShipTo?: boolean;
    showPONumber?: boolean;
    showRequestedDeliveryDate?: boolean;
    showReorderProducts?: boolean;
}

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    // Greif
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    // Greif
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderNumberCell: historyTableProps.orderNumberCell,
    dateCell: historyTableProps.dateCell,
    // Greif Custom Code
    shipToCell: historyTableProps.shipToCell,
    customerPOCell: historyTableProps.customerPOCell,
    statusCell: historyTableProps.statusCell,
    totalCell: historyTableProps.totalCell,
    reorderCells: historyTableProps.buttonCell,
    reorderButton: historyTableProps.button,
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // Greif
    isVmiOrderHistoryPage,
    isReordering,
    isUpdatingCart,
    showOrderNumber,
    showDate,
    showOrderTotal,
    showStatus,
    showShipTo,
    showPONumber,
    showRequestedDeliveryDate,
    showReorderProducts,
    reorder,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    /* Starter Custom Code - Start */
    const orderNumber = orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

    const shipTo = `${order.shipToCity} ${order.shipToPostalCode}`; // Greif Removed stCompanyName stAddress1 stAddress2 shipToState and added postal code

    /* Greif Custom Code - Start */
    const reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) =>
        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });

    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        otherProps.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: otherProps.history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: addToCartPopupTimeout,
        });
    };

    const onReorderError = (message: string) => {
        otherProps.toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: addToCartPopupTimeout,
        });
    };
    /* Greif Custom Code - End */

    return (
        <DataTableRow key={order.id}>
            {/* Greif Custom Code - Start */}
            {isTrueOrUndefined(showOrderNumber) && (
                <DataTableCell {...styles.orderNumberCell} data-test-selector="orderHistoryTable_tableCell_orderNumber">
                    {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
                </DataTableCell>
            )}
            {isTrueOrUndefined(showPONumber) && (
                <DataTableCell {...styles.customerPOCell}>{order.customerPO}</DataTableCell>
            )}
            {isTrueOrUndefined(showDate) && (
                <DataTableCell {...styles.dateCell} data-test-selector="orderHistoryTable_tableCell_date">
                    {orderDateDisplay}
                </DataTableCell>
            )}
            {isTrueOrUndefined(showRequestedDeliveryDate) !== null && (
                <DataTableCell
                    {...styles.dateCell}
                    data-test-selector="orderHistoryTable_tableCell_requestedDeliveryDateDisplay"
                >
                    {order.requestedDeliveryDateDisplay
                        ? getLocalizedDateTime({
                              dateTime: new Date(order.requestedDeliveryDateDisplay),
                              language,
                              options: {
                                  year: "numeric",
                                  month: "numeric",
                                  day: "numeric",
                              },
                          })
                        : null}
                </DataTableCell>
            )}
            {isTrueOrUndefined(showShipTo) && <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>}
            {/* Greif Moved from bottom ---> */}
            {isTrueOrUndefined(showOrderTotal) && (
                <DataTableCell {...styles.totalCell}>{order.orderTotalDisplay}</DataTableCell>
            )}

            {isTrueOrUndefined(showStatus) && (
                <DataTableCell {...styles.statusCell} data-test-selector="orderHistoryTable_tableCell_status">
                    {order.statusDisplay || translate("--")}
                </DataTableCell>
            )}
            {/* Greif Moved up - order.orderTotalDisplay */}
            {isTrueOrUndefined(showReorderProducts) && !isVmiOrderHistoryPage && (
                <DataTableCell {...styles.reorderCells}>
                    {/* Starter Custom Code */}
                    <Button
                        disabled={!order.canAddToCart || Object.keys(isReordering).length > 0 || isUpdatingCart}
                        {...styles.reorderButton}
                        onClick={() => reorderClick(orderNumber, orderNumber, !!order.vmiLocationId)}
                        data-test-selector="orderHistoryTable_Reorder"
                        isLoading={isReordering[orderNumber] as boolean}
                    >
                        {translate("Reorder")}
                    </Button>
                    {/* Starter Custom Code */}
                </DataTableCell>
            )}
            {/* Greif Custom Code - End */}
        </DataTableRow>
    );
    /* Starter Custom Code - End */
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withToaster(withHistory(OrderSummaryCard))); /* Greif Custom Code */
